import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	Directive,
	HostListener,
	inject,
	Input,
	NgModule,
	OnInit,
	signal,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawer, MatDrawerContainer, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslocoModule } from '@jsverse/transloco';
import { delay, filter, of, switchMap } from 'rxjs';

import { UserFeatureFlags } from '@reach/interfaces/user/user-feature-flags';

import { ClientScreen, ClientService, SessionService } from '~app-client/core/services';
import { HomeService } from '~app-client/home/services';
import { MenuPageSpecialKey } from '~app-client/node-creation-new/types';
import { OnboardingModule } from '~app-client/onboarding/modules';
import { OnboardingService } from '~app-client/onboarding/services';
import { OnboardingCondition } from '~app-client/onboarding/types';
import { TeleporterHubModule } from '~app-client/teleporter/components';
import { SidenavService } from '~app-client-item/home/services/sidenav.service';
import { NewNodeCreationService } from '~app-client-item/node-creation-new/services/node-creation.service';
import { ReachUserThumbnailModule } from '~app-ui/thumbnails/users';
import { UiNewButtons } from '~app-ui-new/buttons';
import { UiNewLayouts } from '~app-ui-new/layout';
import { ReachMenuComponent, ReachMenuModule } from '~app-ui-new/menu';
import { SourcePreviewService } from '~ng-common-lib/source-preview/services/source-preview.service';
import { MenuDesktopType } from '~ng-common-lib/ui/menu/types/desktop';
import { TooltipsModule } from '~ng-common-lib/ui/tooltips';

import { WidthSize } from '../../../../../ui-new/menu/components/floating/floating.component';
import { SidenavFooterComponent } from '../sidenav-footer/sidenav-footer.component';
import { SidenavHeaderComponent } from '../sidenav-header/sidenav-header.component';

const CONTEXT_LAYER_ROUTE_PREFIXES = [
	'/home/taxonomies',
	'/home/categories',
	'/home/properties',
	'/home/relationships',
] as const;

@Directive({
	selector: '[sidenavCollapsed]',
	standalone: true,
})
export class HomeSidenavCollapsedDirective implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly templateRef = inject(TemplateRef);
	private readonly viewContainer = inject(ViewContainerRef);
	private readonly cdRef = inject(ChangeDetectorRef);

	private readonly sidenav = inject(HomeSidenavComponent, { optional: true });

	private sidenavService = inject(SidenavService);

	private hasView = false;

	@Input('sidenavCollapsed')
	public condition = true;

	ngOnInit(): void {
		if (!this.sidenav) {
			console.error('No sidenav found');
			return;
		}

		this.sidenav.clientScreen$
			.pipe(
				switchMap((screen) => {
					if (screen === ClientScreen.MOBILE) {
						return of(false);
					}

					return this.sidenavService.collapsed$;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((collapsed) => {
				const condition = this.condition === collapsed;

				if (condition && !this.hasView) {
					this.viewContainer.createEmbeddedView(this.templateRef);
					this.hasView = true;
				} else if (!condition && this.hasView) {
					this.viewContainer.clear();
					this.hasView = false;
				}

				this.cdRef.detectChanges();
			});
	}
}

@NgModule({
	imports: [HomeSidenavCollapsedDirective],
	exports: [HomeSidenavCollapsedDirective],
})
export class HomeSidenavHelpers {}

@Component({
	selector: 'home-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	providers: [SourcePreviewService],
	imports: [
		CommonModule,
		TranslocoModule,
		MatSidenavModule,
		OnboardingModule,
		UiNewButtons,
		UiNewLayouts,
		RouterModule,
		TeleporterHubModule,
		ReachUserThumbnailModule,

		HomeSidenavHelpers,
		SidenavHeaderComponent,
		SidenavFooterComponent,
		TooltipsModule,
		ReachMenuModule,
		ReachMenuComponent,
	],
})
export class HomeSidenavComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private animationFrameId: number | null = null;

	@ViewChild('matDrawerContainer')
	private readonly matDrawerContainer?: MatDrawerContainer;

	private readonly sourcePreviewService = inject(SourcePreviewService);
	private readonly nodeCreationService = inject(NewNodeCreationService);
	private readonly sessionService = inject(SessionService);
	private readonly homeService = inject(HomeService);
	private readonly onboardingService = inject(OnboardingService);
	public readonly sidenavService = inject(SidenavService);
	public readonly isDesktop$ = inject(ClientService).isDesktop$;
	public readonly clientScreen$ = inject(ClientService).screen$;

	public readonly user$ = this.sessionService.user$;

	public readonly sidenavOpened$ = this.homeService.sidenavOpened$;

	public readonly clientScreens = ClientScreen;
	public readonly visible$ = this.homeService.sidenavVisible$;
	public readonly MenuDesktopType = MenuDesktopType;
	public readonly WidthSize = WidthSize;

	private readonly nativeApp = Capacitor.isNativePlatform();
	private readonly hasWidgetFF = this.sessionService.hasFeatureFlag(UserFeatureFlags.CHAT_WIDGET);
	public readonly showMyGenie = !this.nativeApp && this.hasWidgetFF;
	public readonly showTaxonomies = this.sessionService.hasFeatureFlag(UserFeatureFlags.TAXONOMY);

	private readonly $url = signal(this.router.url);
	private readonly $contextLayerManuallyExpanded = signal<boolean | null>(null);
	public readonly $menuContentCollapsed = signal(this.sidenavService.collapsed$$.value);
	public readonly $submenuEnterCollapsed = signal(false);

	public readonly $contextLayerActive = computed(() => {
		const url = this.$url();
		return CONTEXT_LAYER_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
	});

	public readonly $contextLayerExpanded = computed(() => {
		const manual = this.$contextLayerManuallyExpanded();
		if (manual !== null) {
			return manual;
		}
		return this.$contextLayerActive();
	});

	constructor() {
		this.sourcePreviewService.callbackOnOpen(() => {
			this.onboardingService.addCondition(OnboardingCondition.FULL_PAGE_OPENED);
		});

		this.sourcePreviewService.callbackOnClose(() => {
			this.onboardingService.removeCondition(OnboardingCondition.FULL_PAGE_OPENED);
		});

		this.destroyRef.onDestroy(() => {
			this.stopContentSync();
		});

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((event) => {
				this.$url.set(event.urlAfterRedirects);
				if (
					CONTEXT_LAYER_ROUTE_PREFIXES.some((prefix) =>
						event.urlAfterRedirects.startsWith(prefix)
					)
				) {
					this.$contextLayerManuallyExpanded.set(null);
				}
			});
	}

	public toggleContextLayer(): void {
		this.$contextLayerManuallyExpanded.set(!this.$contextLayerExpanded());
	}

	public openContextLayer(event: Event): void {
		event.stopPropagation();
		this.$contextLayerManuallyExpanded.set(true);
		const firstRoute = this.showTaxonomies
			? ['/', 'home', 'taxonomies']
			: ['/', 'home', 'categories'];
		void this.router.navigate(firstRoute);
	}

	ngOnInit(): void {
		this.sidenavService.collapsed$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((collapsed) => {
				if (!collapsed) {
					if (this.$contextLayerExpanded()) {
						this.$submenuEnterCollapsed.set(true);
					}
					this.$menuContentCollapsed.set(false);
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							this.$submenuEnterCollapsed.set(false);
						});
					});
				} else {
					this.$submenuEnterCollapsed.set(false);
					window.setTimeout(() => {
						if (this.sidenavService.collapsed$$.value) {
							this.$menuContentCollapsed.set(true);
						}
					}, 400);
				}

				this.startContentSync();
				this.updateDrawerMargins();
				requestAnimationFrame(() => {
					this.updateDrawerMargins();
				});
			});
		this.visible$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.updateDrawerMargins();
			requestAnimationFrame(() => {
				this.updateDrawerMargins();
			});
		});

		this.isDesktop$
			.pipe(takeUntilDestroyed(this.destroyRef), delay(1000))
			.subscribe((isDesktop) => {
				if (!isDesktop) {
					this.sidenavService.toggleCollapsedStatus(false);
				}
			});
	}

	public onBackdropClick(): void {
		this.homeService.setSidenavOpened(false);
	}

	public createNode(): void {
		void this.nodeCreationService.openCreation(MenuPageSpecialKey.NODE_TYPE_SELECTOR);
	}

	public onOpenedChange(drawer: MatDrawer): void {
		const mode = drawer.mode;
		const opened = drawer.opened;

		const addCondition = mode === 'push' && opened;

		if (addCondition) {
			this.onboardingService.addCondition(OnboardingCondition.SIDE_MENU_OPENED);
		} else {
			this.onboardingService.removeCondition(OnboardingCondition.SIDE_MENU_OPENED);
		}

		this.updateDrawerMargins();
	}

	public onDrawerTransitionStart(event: TransitionEvent): void {
		if (!this.isDrawerSizeTransition(event)) {
			return;
		}

		this.startContentSync();
	}

	public onDrawerTransitionEnd(event: TransitionEvent): void {
		if (!this.isDrawerSizeTransition(event)) {
			return;
		}

		this.stopContentSync();
		this.$menuContentCollapsed.set(this.sidenavService.collapsed$$.value);
		this.updateDrawerMargins();
	}

	@HostListener('window:resize')
	@HostListener('window:orientationchange')
	public updateDrawerMargins(): void {
		requestAnimationFrame(() => {
			this.matDrawerContainer?.updateContentMargins();
		});
	}

	private isDrawerSizeTransition(event: TransitionEvent): boolean {
		return ['width', 'min-width', 'max-width'].includes(event.propertyName);
	}

	private startContentSync(): void {
		if (this.animationFrameId !== null) {
			return;
		}

		const syncContentPosition = (): void => {
			this.matDrawerContainer?.updateContentMargins();
			this.animationFrameId = requestAnimationFrame(syncContentPosition);
		};

		this.animationFrameId = requestAnimationFrame(syncContentPosition);
	}

	private stopContentSync(): void {
		if (this.animationFrameId === null) {
			return;
		}

		cancelAnimationFrame(this.animationFrameId);
		this.animationFrameId = null;
	}
}
